import { useCallback, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from 'react-router-dom'
import { useT } from '../i18n/useT'
import { useCredits } from '../hooks/useCredits'
import { useLemonSqueezy } from '../hooks/useLemonSqueezy'
import { trackPageViewed, identifyUser, setSubscriptionProps, trackCheckoutResumed } from '../analytics/posthog'
import { takePendingPlan } from '../lib/pendingCheckout'
import { Toast } from '../components/ui/Toast'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { HeroSection } from '../components/hero/HeroSection'
import { TranscriptionCard } from '../components/transcription/TranscriptionCard'
import { HowItWorksSection } from '../components/landing/HowItWorksSection'
import { PrivacySection } from '../components/privacy/PrivacySection'
import { FaqSection } from '../components/landing/FaqSection'
import { PricingSection } from '../components/pricing/PricingSection'
import { LimitReachedModal } from '../components/subscription/LimitReachedModal'
import { CancelSubscriptionModal } from '../components/subscription/CancelSubscriptionModal'

export function IndexPage() {
  const t = useT()
  const { isAuthenticated, user } = useAuth0()
  const {
    credits, hasCredits, loading: creditsLoading, updateFromResult, markAnonExhausted,
    initializeOnLogin, refresh,
    isSubscribed, planName, monthlyBalance, renewsOn, bonusDeclined,
  } = useCredits()
  const { openCheckout, startPolling } = useLemonSqueezy({
    onCreditsUpdated: refresh,
    isSubscribed,
    planName,
    credits,
  })
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showCheckoutError, setShowCheckoutError] = useState(false)
  // Plan pressed before sign-in — the checkout opens by itself once the user
  // is back; the toast covers the moment the URL is being fetched
  const [resumingPlan, setResumingPlan] = useState<string | null>(null)
  const [paymentReturned, setPaymentReturned] = useState(false)

  // Initialize on login (the API client's token getter is wired app-wide
  // by AuthTokenBridge in App.tsx)
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      initializeOnLogin()
      // Auth0 sub as distinct_id (stable, matches the server-side webhook
      // events); the email is just a person property
      identifyUser(user.sub, { email: user.email })
    }
  }, [isAuthenticated, user?.sub]) // eslint-disable-line react-hooks/exhaustive-deps

  // Subscription person-props follow /auth/me instead of being hardcoded
  useEffect(() => {
    if (isAuthenticated) setSubscriptionProps(isSubscribed, planName)
  }, [isAuthenticated, isSubscribed, planName])

  // Back from Auth0 with a plan pressed before sign-in: go straight to the
  // payment page — no extra click, no extra screen. Same-tab navigation,
  // because an automatic window.open outside a gesture is popup-blocked.
  useEffect(() => {
    if (!isAuthenticated) return
    const plan = takePendingPlan()
    if (!plan) return
    trackCheckoutResumed(plan)
    setResumingPlan(plan)
    document.getElementById('pricing')?.scrollIntoView()
    openCheckout(plan, { sameTab: true }).catch(() => {
      setResumingPlan(null)
      setShowCheckoutError(true)
    })
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Back from Lemon's hosted checkout (the product's return URL is
  // /?checkout=success): confirm, refresh, and poll until the webhook has
  // credited the account. Unknown/absent values are ignored.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== 'success') return
    params.delete('checkout')
    const rest = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`)
    setPaymentReturned(true)
    if (isAuthenticated) {
      void refresh()
      startPolling()
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track page view
  useEffect(() => {
    trackPageViewed()
  }, [])

  // The static <title> in index.html is English — follow the UI language
  useEffect(() => {
    document.title = t('idx.docTitle')
  }, [t])

  // The browser can't scroll to a #hash on load — sections render after the
  // document's anchor pass — so restore the target once they're mounted.
  // Keyed on the hash rather than on mount alone, because coming back from
  // Auth0 the hash arrives LATER: the page loads at /?code=…, and only once
  // the SDK has traded the code does onRedirectCallback navigate to the
  // returnTo. On mount there is nothing to scroll to yet.
  const { hash } = useLocation()
  useEffect(() => {
    const id = hash.slice(1)
    if (id) document.getElementById(id)?.scrollIntoView()
  }, [hash])

  const handleCreditsUpdate = useCallback(
    (remaining: number | null) => {
      updateFromResult(remaining)
    },
    [updateFromResult],
  )

  // The backend said "no credits" (403) — for anonymous users that's the
  // per-IP cap a cleared localStorage can't see, so sync the local counter
  const handleServerLimit = useCallback(() => {
    if (isAuthenticated) {
      void refresh()
    } else {
      markAnonExhausted()
    }
    setShowLimitModal(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, refresh])

  async function handleSubscribe(planName: string) {
    try {
      await openCheckout(planName)
    } catch {
      // Anonymous visitors never reach this (PlanCard sends them to login),
      // so this is a real failure — say so instead of silently blinking
      setShowCheckoutError(true)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header credits={credits} monthlyCredits={monthlyBalance} />
      <main className="flex-1">
        <HeroSection />
        <TranscriptionCard
          credits={credits}
          monthlyCredits={monthlyBalance}
          hasCredits={hasCredits}
          isSubscribed={isSubscribed}
          planName={planName}
          onCreditsUpdate={handleCreditsUpdate}
          onLimitReached={() => setShowLimitModal(true)}
          onServerLimit={handleServerLimit}
        />
        {/* Sections alternate surface/surface-alt backgrounds — pricing sits
            between the two card grids so the landing doesn't read as one
            repeating 3-card pattern */}
        <HowItWorksSection />
        <PricingSection
          onSubscribe={handleSubscribe}
          onCancelSubscription={() => setShowCancelModal(true)}
          currentPlan={planName}
          isSubscribed={isSubscribed}
          credits={credits}
          creditsLoading={creditsLoading}
          renewsOn={renewsOn}
        />
        <PrivacySection />
        <FaqSection />
      </main>
      <Footer />
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        isSubscribed={isSubscribed}
        renewsOn={renewsOn}
        bonusDeclined={bonusDeclined}
      />
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelled={refresh}
      />
      <Toast
        message={t('pricing.openingPayment', { plan: resumingPlan ?? '' })}
        isVisible={!!resumingPlan}
        onHide={() => setResumingPlan(null)}
        duration={8000}
      />
      <Toast
        message={t('pricing.paymentReceived')}
        isVisible={paymentReturned}
        onHide={() => setPaymentReturned(false)}
        duration={6000}
      />
      <Toast
        message={t('err.checkout')}
        isVisible={showCheckoutError}
        onHide={() => setShowCheckoutError(false)}
        duration={4000}
      />
    </div>
  )
}
